import { useGetPartnerQuery } from '@/api/api.slice';
import { useAppDispatch, useAppSelector } from '@/app/hooks.ts';
import { useCreateSetupIntentMutation } from '@/features/means-of-payments/api/useCreateSetupIntentMutation.ts';
import { useCreateWireTransferSetupMutation } from '@/features/means-of-payments/api/useCreateWireTransferSetupMutation.ts';
import { useFinalizePaymentMutation } from '@/features/means-of-payments/api/useFinalizePaymentMutation.ts';
import { useGetAvailablePaymentListQuery } from '@/features/means-of-payments/api/useGetAvailablePaymentListQuery.ts';
import {
  getMeansOfPaymentsState,
  setDialogAddPaymentMethodOpen,
} from '@/features/means-of-payments/MeansOfPayments.slice.ts';
import { WireTransferElement } from '@/features/means-of-payments/WireTransferElement.tsx';
import {
  CollectMeanOfPayment,
  Dialog,
  getMessageFromError,
  PaymentContextProvider,
  PossibleLocalesType,
  toastError,
} from '@arsnovasystems/ars-front-lib';
import { useTranslation } from 'react-i18next';

export const AddMeanOfPaymentDialog = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const stripePublicKey = import.meta.env.ARS_STRIPE_PUBLIC_KEY;
  const [createIntentTrigger] = useCreateSetupIntentMutation();
  const [finalizeIntentTrigger] = useFinalizePaymentMutation();
  const [createWireTransferSetupTrigger] = useCreateWireTransferSetupMutation();
  const { dialogAddPaymentMethodOpen } = useAppSelector(
    getMeansOfPaymentsState,
  );

  const { data: me } = useGetPartnerQuery();
  const { data: availablePaymentList } = useGetAvailablePaymentListQuery();

  if (!me || !availablePaymentList) return null;

  const billingDetails = {
    name: me.lastName,
    phone: me.phoneNumber,
    email: me.email,
    address: {
      country: me.country?.countryCode ?? undefined,
      line1: me.address,
      city: me.town,
      postal_code: me.zipCode,
      state: me.stateRegion,
    },
  };

  return (
    <Dialog
      title={t('ik.add.a.payment.mean')}
      open={dialogAddPaymentMethodOpen}
      onClose={() => dispatch(setDialogAddPaymentMethodOpen(false))}
      sx={{
        minWidth: 600,
      }}
    >
      <PaymentContextProvider
        createIntentMutationTrigger={createIntentTrigger}
        finalizeIntentMutationTrigger={finalizeIntentTrigger}
        WireTransferElement={WireTransferElement}
        locale={i18n.language as PossibleLocalesType}
        currency={me.currency}
        stripePublicKey={stripePublicKey}
        billingDetails={billingDetails}
        possiblePaymentList={availablePaymentList.availablePayments}
        onSuccess={async (paymentType) => {
          // Card / SEPA are already collected and finalized by the library
          // before this callback fires; wire transfer, however, is now owned
          // by the consumer, so we provision the funding setup here.
          if (paymentType === 'wire_transfer') {
            const result = await createWireTransferSetupTrigger();
            if (result.error) {
              toastError({ description: getMessageFromError(result.error) });
              return;
            }
          }
          dispatch(setDialogAddPaymentMethodOpen(false));
        }}
      >
        <CollectMeanOfPayment />
      </PaymentContextProvider>
    </Dialog>
  );
};
